import React, {
  type ForwardedRef,
  type PropsWithChildren,
  useMemo,
} from "react";

import clsx from "clsx";

import { type TableTreeColumn } from "@devexpress/dx-react-grid-bootstrap4";
import { type Dimensions } from "@features/sales-v3/charts/chart";
import { usePageConfiguration } from "@features/sales-v3/hooks";

import { SELECTION_COLUMN_WIDTH } from "../constants";

interface Props extends PropsWithChildren<TableTreeColumn.CellProps> {
  forwardedRef?: ForwardedRef<HTMLTableCellElement>;
}

export const TableTreeColumnCell = ({
  column,
  children,
  tableRow,
  tableColumn,
  row,
  forwardedRef,
  ...restProps
}: Props) => {
  const { breakdowns, activeTab } = usePageConfiguration();
  const currentDepth = breakdowns.findIndex(
    (b) => b.key === row.rowMetadata.breakdown,
  );

  const noBreakdowns = useMemo(() => {
    const parsedKey = JSON.parse(row.key);
    const noBreakdowns = Array.isArray(parsedKey)
      ? (parsedKey as Dimensions[]).every((k) => !Object.keys(k).length) ||
        (breakdowns.length > 0 &&
          currentDepth !== Object.keys(row.rowMetadata.dimensions).length - 1)
      : false;
    return noBreakdowns;
  }, [breakdowns.length, currentDepth, row.key, row.rowMetadata.dimensions]);

  const reachedEndOfBreakdowns = useMemo(() => {
    if (activeTab === "breakdown") {
      return breakdowns.length === currentDepth + 1;
    }

    return breakdowns.length === currentDepth;
  }, [activeTab, breakdowns.length, currentDepth]);

  return (
    <td
      ref={forwardedRef}
      {...restProps}
      className="border-right dx-g-bs4-fixed-cell position-sticky cell-clickable group/row"
      style={{ left: `${SELECTION_COLUMN_WIDTH}px` }}
    >
      <div
        className={clsx("flex h-full px-2", {
          "ml-8": noBreakdowns && !reachedEndOfBreakdowns,
        })}
      >
        {children}
      </div>
    </td>
  );
};
